import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Settings, Check } from 'lucide-react';
import { 
  getCookieConsent, 
  saveCookieConsent, 
  initializeTracking,
  DEFAULT_COOKIE_SETTINGS 
} from '../utils/cookieUtils';

const CookieManager = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [cookieSettings, setCookieSettings] = useState(() => {
    const saved = getCookieConsent();
    return saved || DEFAULT_COOKIE_SETTINGS;
  });

  const handleToggleSetting = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    
    setCookieSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveSettings = () => {
    saveCookieConsent(cookieSettings);
    onClose();
    
    // Initialize tracking based on settings
    initializeTracking();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4">
      <div className="bg-black border-2 border-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-white font-bold text-2xl">
              {t('cookies.settingsTitle')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <p className="text-gray-300 mb-6 leading-relaxed">
            {t('cookies.description')}
          </p>
          
          <div className="space-y-4 mb-8">
            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700">
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-2">
                  {t('cookies.necessary.title')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('cookies.necessary.description')}
                </p>
              </div>
              <div className="ml-4">
                <div className="w-12 h-6 bg-white rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-black rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
                <span className="text-xs text-gray-400 mt-1 block text-center">
                  {t('cookies.required')}
                </span>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700">
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-2">
                  {t('cookies.analytics.title')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('cookies.analytics.description')}
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleToggleSetting('analytics')}
                  className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
                    cookieSettings.analytics 
                      ? 'bg-white justify-end' 
                      : 'bg-gray-600 justify-start'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full transition-colors duration-200 mx-1 ${
                    cookieSettings.analytics ? 'bg-black' : 'bg-gray-400'
                  }`} />
                </button>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700">
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-2">
                  {t('cookies.marketing.title')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('cookies.marketing.description')}
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleToggleSetting('marketing')}
                  className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
                    cookieSettings.marketing 
                      ? 'bg-white justify-end' 
                      : 'bg-gray-600 justify-start'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full transition-colors duration-200 mx-1 ${
                    cookieSettings.marketing ? 'bg-black' : 'bg-gray-400'
                  }`} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-transparent border border-gray-400 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200"
            >
              {t('cookies.cancel')}
            </button>
            <button
              onClick={handleSaveSettings}
              className="px-6 py-2 bg-white text-black hover:bg-gray-200 transition-colors duration-200 font-bold"
            >
              {t('cookies.saveSettings')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieManager;